import { Cart } from 'types/entity/cart';
import { UseCartReturn } from 'frontastic/hooks/useCart/types';
import { ShippingMethod } from 'shared/types/cart/CustomShippingMethod';
import { calculateSubtotalAmount } from './calculateSubTotalAmount';
import { calculateDiscountedAmount } from './calculateDiscountedAmount';

type MapCosts = (props: {
  cart?: Partial<Cart>;
  currency?: string;
  shippingMethod?: ShippingMethod;
}) => UseCartReturn['transaction'];

const mapCosts: MapCosts = ({ cart: data, currency = 'USD', shippingMethod }) => {
  if (!data?.sum?.centAmount) {
    return {
      subtotal: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      discount: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      totalNoTax: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      tax: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      shipping: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
      total: { centAmount: 0, currencyCode: currency, fractionDigits: 2 },
    };
  }

  const currencyCode = data.sum?.currencyCode ?? currency;
  const fractionDigits = data.sum?.fractionDigits ?? 2;

  const totalAmount = data.sum?.centAmount as number;
  const subTotalAmount = calculateSubtotalAmount(data.lineItems);

  const discountedAmount = calculateDiscountedAmount(data);

  const totalTax = totalAmount > 0 ? (data.taxed?.taxAmount?.centAmount ?? 0) : 0;

  const totalShipping = shippingMethod
    ? (shippingMethod?.estimatedCostNumericCentAmount ?? 0)
    : (data.shippingInfo?.price?.centAmount ?? 0);

  const isEstimatedShipping = !!shippingMethod;

  const totalWithoutTaxes = totalAmount + (isEstimatedShipping ? totalShipping : 0);

  return {
    subtotal: {
      centAmount: subTotalAmount,
      currencyCode,
      fractionDigits,
    },
    discount: {
      centAmount: discountedAmount,
      currencyCode,
      fractionDigits,
    },
    shipping: {
      centAmount: totalShipping,
      currencyCode,
      fractionDigits,
    },
    isEstimatedShipping,
    totalNoTax: {
      centAmount: totalWithoutTaxes,
      currencyCode,
      fractionDigits,
    },
    tax: {
      centAmount: totalTax,
      currencyCode,
      fractionDigits,
    },
    total: {
      centAmount: totalAmount + (isEstimatedShipping ? totalShipping : 0) + totalTax,
      currencyCode,
      fractionDigits,
    },
  };
};

export default mapCosts;
